'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

// useLayoutEffect on the client, useEffect on the server (avoids the SSR warning while
// keeping the synchronous, before-paint timing the ref sync below relies on).
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
interface UseInfiniteTableScrollParams {
  /**
   * The active `useInfiniteQuery` key. The scroll container is reset to the top
   * whenever this changes — i.e. whenever the filters or sort that define the
   * dataset change. Every filter and the sort feed into this key, so reacting to
   * it covers them all without per-filter wiring.
   */
  queryKey: unknown;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** True while the whole table shows its loading skeleton (filter refetch / transition). */
  isLoadingFullTable: boolean;
  fetchNextPage: () => void;
  /** Disable the sentinel observer (e.g. the in-wallet empty state). */
  enabled: boolean;
  /** Re-attach the observer when the layout swaps the sentinel (mobile vs desktop). */
  isMobile: boolean;
}

/**
 * Shared infinite-scroll wiring for the explore tables (vaults & markets).
 *
 * Owns the sentinel + scroll-container refs and three concerns:
 *  1. prefetch the next page when the bottom sentinel scrolls into view,
 *  2. keep filling while the sentinel stays in view after a fetch settles,
 *  3. reset the scroll position to the top when the query key (filters/sort) changes.
 *
 * (3) is the fix for the reload-loop: without it, changing a filter while scrolled
 * down leaves the sentinel parked near the bottom of the now-short list, so the
 * observer cascades many `fetchNextPage` calls. Resetting to the top leaves only the
 * single intended prefetch. The `!isLoadingFullTable` guard defers (never drops)
 * prefetch while the new first page is loading.
 */
export function useInfiniteTableScroll(t0) {
  const $ = _c(27);
  const {
    queryKey,
    hasNextPage,
    isFetchingNextPage,
    isLoadingFullTable,
    fetchNextPage,
    enabled,
    isMobile
  } = t0;
  const sentinelRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  const isLoadingFullTableRef = useRef(isLoadingFullTable);
  const isIntersectingRef = useRef(false);
  let t1;
  if ($[0] !== hasNextPage || $[1] !== isFetchingNextPage || $[2] !== isLoadingFullTable) {
    t1 = () => {
      hasNextPageRef.current = hasNextPage;
      isFetchingNextPageRef.current = isFetchingNextPage;
      isLoadingFullTableRef.current = isLoadingFullTable;
    };
    $[0] = hasNextPage;
    $[1] = isFetchingNextPage;
    $[2] = isLoadingFullTable;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useIsomorphicLayoutEffect(t1);
  let t2;
  if ($[4] !== fetchNextPage) {
    t2 = () => {
      if (isIntersectingRef.current && hasNextPageRef.current && !isFetchingNextPageRef.current && !isLoadingFullTableRef.current) {
        fetchNextPage();
      }
    };
    $[4] = fetchNextPage;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const maybeFetchNextPage = t2;
  let t3;
  if ($[6] !== enabled || $[7] !== maybeFetchNextPage) {
    t3 = () => {
      const sentinel = sentinelRef.current;
      if (!sentinel || !enabled) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        isIntersectingRef.current = entries[0]?.isIntersecting ?? false;
        maybeFetchNextPage();
      }, {
        root: scrollContainerRef.current,
        rootMargin: `${scrollContainerRef.current?.clientHeight ?? 800}px`
      });
      observer.observe(sentinel);
      return () => observer.disconnect();
    };
    $[6] = enabled;
    $[7] = maybeFetchNextPage;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== enabled || $[10] !== hasNextPage || $[11] !== isMobile || $[12] !== maybeFetchNextPage) {
    t4 = [maybeFetchNextPage, enabled, isMobile, hasNextPage];
    $[9] = enabled;
    $[10] = hasNextPage;
    $[11] = isMobile;
    $[12] = maybeFetchNextPage;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  if ($[14] !== maybeFetchNextPage) {
    t5 = () => {
      maybeFetchNextPage();
    };
    $[14] = maybeFetchNextPage;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== hasNextPage || $[17] !== isFetchingNextPage || $[18] !== isLoadingFullTable || $[19] !== maybeFetchNextPage) {
    t6 = [maybeFetchNextPage, isFetchingNextPage, hasNextPage, isLoadingFullTable];
    $[16] = hasNextPage;
    $[17] = isFetchingNextPage;
    $[18] = isLoadingFullTable;
    $[19] = maybeFetchNextPage;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  useEffect(t5, t6);
  let t7;
  if ($[21] !== queryKey) {
    t7 = JSON.stringify(queryKey);
    $[21] = queryKey;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const queryKeySignature = t7;
  const prevKeySignatureRef = useRef(queryKeySignature);
  let t8;
  let t9;
  if ($[23] !== queryKeySignature) {
    t8 = () => {
      if (prevKeySignatureRef.current === queryKeySignature) {
        return;
      }
      prevKeySignatureRef.current = queryKeySignature;
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTo({
          top: 0
        });
      } else {
        window.scrollTo({
          top: 0
        });
      }
    };
    t9 = [queryKeySignature];
    $[23] = queryKeySignature;
    $[24] = t8;
    $[25] = t9;
  } else {
    t8 = $[24];
    t9 = $[25];
  }
  useEffect(t8, t9);
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      sentinelRef,
      scrollContainerRef
    };
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}